import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { translations, format, type UiLanguage } from "./translations";

export type { UiLanguage };

const STORAGE_KEY = "ma-lite-ui-language";

function detectInitialLanguage(): UiLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "tr") return stored;
  } catch {
    // storage unavailable — fall through to browser detection
  }
  const browserLang = (navigator.language || "en").toLowerCase();
  return browserLang.startsWith("tr") ? "tr" : "en";
}

/** Reads a nested value from the translation dictionary by dot-path, e.g. "settings.license.title". */
function resolve(lang: UiLanguage, path: string): unknown {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let node: any = translations[lang];
  for (const part of path.split(".")) {
    node = node?.[part];
    if (node === undefined) return undefined;
  }
  return node;
}

interface LanguageContextValue {
  language: UiLanguage;
  setLanguage: (lang: UiLanguage) => void;
  /** Translates a dot-path key, e.g. t("settings.title"). Optionally interpolates {placeholders}. */
  t: (path: string, values?: Record<string, string | number>) => string;
  /** Returns a raw (non-string) translation node, e.g. an array of onboarding steps. */
  tRaw: <T = unknown>(path: string) => T;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<UiLanguage>(detectInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: UiLanguage) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore — language just won't persist across reloads
    }
  }, []);

  const t = useCallback(
    (path: string, values?: Record<string, string | number>): string => {
      const raw = resolve(language, path) ?? resolve("en", path);
      const text = typeof raw === "string" ? raw : path;
      return values ? format(text, values) : text;
    },
    [language],
  );

  const tRaw = useCallback(
    <T,>(path: string): T => (resolve(language, path) ?? resolve("en", path)) as T,
    [language],
  );

  const value = useMemo(() => ({ language, setLanguage, t, tRaw }), [language, setLanguage, t, tRaw]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Access the current UI language, switch it, and translate strings. */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
}
